import React, { useState } from "react";
import { motion } from "framer-motion";
import {
  Sun,
  Leaf,
  Briefcase,
  GraduationCap,
  Award,
  Users,
  Image as ImageIcon,
  MapPin,
  Mail,
  Phone,
  File,
  Download,
  Cpu,
  Factory,
} from "lucide-react";

/* Divider Component */
const Divider = () => (
  <div className="my-6 h-1 w-24 bg-gradient-to-r from-green-600 to-green-300 rounded-full" />
);
const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: "easeOut" },
  },
};

const container = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.12,
      delayChildren: 0.1,
    },
  },
};

const item = {
  hidden: { opacity: 0, y: 32 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: "easeOut" },
  },
};


/* Course Structure */
const year1 = [
  "Mathematics for Engineering",
  "Basic Electrical Engineering",
  "Introduction to Renewable Energy",
  "Engineering Drawing",
  "Workshop Practice",
  "Computer Applications",
];

const year2 = [
  "Solar PV System Design",
  "Wind & Hydro Energy Systems",
  "Energy Storage Technologies",
  "Power Electronics",
  "Energy Audit & Management",
  "SIWES / Final Project",
];

/* Staff */
const staffList = [
  {
    name: "Engr. Dr. M. I. Ibrahim",
    role: "Head of Department",
    img: "/images/hodurp.png",
  },
  {
    name: "Engr. A. Bello",
    role: "Senior Lecturer (Solar Energy)",
    img: "/images/staff1.jpg",
  },
  {
    name: "Engr. S. Mohammed",
    role: "Lecturer (Power Systems)",
    img: "/images/staff2.jpg",
  },
  {
    name: "Engr. R. Abdullahi",
    role: "Lecturer (Energy Management)",
    img: "/images/staff3.jpg",
  },
];

const brochureSlides = [
  { img: "/images/urpb2.png", title: "Program Overview" },
  { img: "/images/urpg2.png", title: "Labs & Innovation Hub" },
  { img: "/images/tvetg2.jpeg", title: "Certifications Pathway" },
];

const galleryImages = [
  { img: "/images/urpg1.jpg" },
  { img: "/images/urpg2.jpg" },
  { img: "/images/urpg3.jpg" },
  { img: "/images/urpg4.jpg" },
];

const RenewableEnergyEngineering = () => {
  const [activeIndex, setActiveIndex] = useState(null);

  const nextImage = () => {
  setActiveIndex((prev) =>
    prev === galleryImages.length - 1 ? 0 : prev + 1
  );
};

const prevImage = () => {
  setActiveIndex((prev) =>
    prev === 0 ? galleryImages.length - 1 : prev - 1
  );
};

  return (
    <div className="min-h-screen bg-gray-50 text-gray-800">

      <motion.section
  className="relative h-[320px] md:h-[450px] flex items-center justify-center overflow-hidden"
  initial={{ opacity: 0 }}
  animate={{ opacity: 1 }}
>
  <motion.img
    src="/images/urpb1.png"
    alt="Renewable Energy Engineering"
    className="absolute inset-0 w-full h-full object-cover"
    initial={{ scale: 1.1 }}
    animate={{ scale: 1 }}
    transition={{ duration: 6, ease: "easeOut" }}
  />

  <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-black/50 to-black/30" />

        <motion.div
          className="relative text-center text-white px-6"
          initial={{ y: 40, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.3 }}
        >
          <h1 className="text-4xl md:text-6xl font-extrabold mb-4 leading-tight">
            Renewable Energy Engineering
          </h1>

          <p className="text-lg md:text-xl max-w-3xl mx-auto mb-6">
            Powering sustainability, innovation, and Nigeria’s clean energy future.
          </p>

          <div className="flex justify-center gap-4">
            <a href="#admission" className="px-6 py-3 bg-green-600 hover:bg-green-700 rounded-full font-semibold transition">
              Apply Now
            </a>
            <a href="#brochure" className="px-6 py-3 border border-white/60 rounded-full hover:bg-white/10 transition">
              View Brochure
            </a>
          </div>
        </motion.div>
      </motion.section>


      <main className="px-6 md:px-20 py-16 space-y-12">

        {/* DEPARTMENT IN BRIEF */}
        <motion.section
          variants={container}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="bg-white rounded-2xl shadow-lg p-8"
        >
          <motion.h2
            variants={item}
            className="text-2xl font-bold flex items-center gap-3 text-green-700"
          >
            <Leaf /> The Department in Brief
          </motion.h2>

          <motion.div variants={item}>
            <Divider />
          </motion.div>

          <motion.p
            variants={item}
            className="leading-relaxed text-justify text-gray-700"
          >
            The Department of Renewable Energy Engineering was established to address
            Nigeria’s growing demand for sustainable and clean energy solutions. The
            programme equips students with strong theoretical knowledge and practical
            skills in solar, wind, hydro, and bioenergy systems.
          </motion.p>
        </motion.section>

        {/* HOD Speech */}
          <motion.section
            id="hodspeech"
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="relative bg-white rounded-2xl shadow-lg p-8 flex flex-col md:flex-row gap-10 items-start overflow-hidden"
          >
            {/* Decorative Background */}
            <div className="absolute inset-0 bg-gradient-to-b from-green-50 via-white to-green-50 pointer-events-none" />
            <div className="absolute -top-24 -left-24 w-96 h-96 bg-green-200/40 rounded-full blur-3xl" />
            <div className="absolute -bottom-24 -right-24 w-96 h-96 bg-green-300/40 rounded-full blur-3xl" />

            {/* HOD Image */}
            <div className="w-full md:w-1/3 flex justify-center relative z-10">
              <motion.img
                src="/images/hodurp.png"
                alt="Engr. Dr. M. I. Ibrahim"
                className="w-56 h-56 rounded-full object-cover shadow-xl border-4 border-white"
                animate={{ y: [0, -6, 0] }}
                transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
                whileHover={{ scale: 1.08, rotate: 1 }}
              />
            </div>

            {/* Speech Content */}
            <div className="flex-1 relative z-10">
              <h2 className="text-2xl font-bold flex items-center gap-3 text-green-800">
                <Users /> Message from the Head of Department
              </h2>

              <Divider />

              {/* Achievement Badges */}
              <div className="flex flex-wrap gap-3 mb-6">
                <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm shadow hover:scale-105 hover:shadow-md transition">
                  🎓 PhD Mechanical Engineering (ABU Zaria)
                </span>
                <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm shadow hover:scale-105 hover:shadow-md transition">
                  🔬 International Solar Energy Research
                </span>
                <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm shadow hover:scale-105 hover:shadow-md transition">
                  💼 COREN Registered Engineer
                </span>
                <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm shadow hover:scale-105 hover:shadow-md transition">
                  📚 MNSE • MNIM • NIMECHe
                </span>
                <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm shadow hover:scale-105 hover:shadow-md transition">
                  🌍 Renewable Energy Grants & Projects
                </span>
              </div>

              {/* Quote */}
              <div className="bg-green-50 border-l-4 border-green-700 p-4 rounded-md shadow-sm mb-6">
                <p className="italic text-green-900 font-medium">
                  “Renewable energy engineering is not just a profession—it is a commitment
                  to sustainability, innovation, and national development.”
                </p>
              </div>

              {/* Speech Text */}
              <p className="text-gray-700 leading-relaxed text-justify">
                I warmly welcome you to the Department of Renewable Energy Engineering,
                Federal Polytechnic Bida. Our department was established to respond to the
                growing global and national demand for clean, sustainable, and reliable
                energy solutions. We are proud to be at the forefront of training the next
                generation of engineers who will drive Nigeria’s energy transition.
              </p>

              <p className="text-gray-700 mt-4 leading-relaxed text-justify">
                The programme is designed to provide students with a strong foundation in
                solar, wind, hydro, and other emerging renewable energy technologies. Our
                approach combines sound theoretical principles with hands-on practical
                training, research-driven learning, and industry-relevant certifications.
              </p>

              <p className="text-gray-700 mt-4 leading-relaxed text-justify">
                I encourage our students to be innovative, disciplined, and committed to
                excellence. Make full use of our laboratories, workshops, and collaborative
                opportunities. Together, we will continue to build professionals who are
                self-reliant, globally competitive, and capable of contributing meaningfully
                to sustainable development.
              </p>

              <p className="text-green-800 font-semibold mt-6">
                — Engr. Dr. M. I. Ibrahim
              </p>
              <p className="text-gray-600 text-sm">
                Head of Department, Renewable Energy Engineering
              </p>
            </div>
          </motion.section>
        {/* ADMISSION REQUIREMENTS */}
        <motion.section variants={fadeUp} initial="hidden" whileInView="visible" className="bg-white rounded-2xl shadow-lg p-8">
          <h2 className="text-2xl font-bold flex items-center gap-3">
            <GraduationCap /> Admission Requirements
          </h2>
          <Divider />
          <p className="text-justify leading-relaxed">
            Candidates must possess at least five O’Level credits including English Language,
            Mathematics, Physics, Chemistry, and Biology/Agricultural Science at not more than
            two sittings. NABTEB and NTC certificates are acceptable.
          </p>
        </motion.section>

        {/* JOB PROSPECTS */}
        <motion.section variants={fadeUp} initial="hidden" whileInView="visible" className="bg-white rounded-2xl shadow-lg p-8">
          <h2 className="text-2xl font-bold flex items-center gap-3">
            <Briefcase /> Job Prospects
          </h2>
          <Divider />
          <div className="grid md:grid-cols-3 gap-6">
            <motion.div
              whileHover={{ y: -8, scale: 1.03 }}
              className="bg-gradient-to-b from-white to-green-50 p-6 rounded-xl shadow hover:shadow-xl transition text-center"
            >
              <Sun className="mx-auto text-green-600 mb-3" />
              <h4 className="font-semibold">Solar Energy Engineer</h4>
            </motion.div>

            <motion.div
              whileHover={{ y: -8, scale: 1.03 }}
              className="bg-gradient-to-b from-white to-green-50 p-6 rounded-xl shadow hover:shadow-xl transition text-center"
            >
              <Factory className="mx-auto text-green-600 mb-3" />
              <h4 className="font-semibold">Power Systems Technologist</h4>
            </motion.div>

            <motion.div
              whileHover={{ y: -8, scale: 1.03 }}
              className="bg-gradient-to-b from-white to-green-50 p-6 rounded-xl shadow hover:shadow-xl transition text-center"
            >
              <Cpu className="mx-auto text-green-600 mb-3" />
              <h4 className="font-semibold">Energy Consultant</h4>
            </motion.div>
          </div>
        </motion.section>

        {/* CERTIFICATIONS */}
        <motion.section variants={fadeUp} initial="hidden" whileInView="visible" className="bg-green-50 rounded-2xl shadow-lg p-8">
          <h2 className="text-2xl font-bold flex items-center gap-3">
            <Award /> Certifications & Professional Courses
          </h2>
          <Divider />
          <ul className="grid md:grid-cols-2 gap-4">
            <li className="bg-white p-4 rounded shadow">COREN Engineering Pathway</li>
            <li className="bg-white p-4 rounded shadow">Solar PV Installation</li>
            <li className="bg-white p-4 rounded shadow">Energy Audit & Management</li>
            <li className="bg-white p-4 rounded shadow">NABTEB Renewable Energy</li>
          </ul>
        </motion.section>

        {/* MEET OUR STAFF */}
        <motion.section variants={fadeUp} initial="hidden" whileInView="visible" className="bg-white rounded-2xl shadow-lg p-8">
          <h2 className="text-2xl font-bold flex items-center gap-3">
            <Users /> Meet Our Staff
          </h2>
          <Divider />
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {staffList.map((s, i) => (
              <motion.div
                key={i}
                whileHover={{ y: -10 }}
                transition={{ type: "spring", stiffness: 200 }}
                className="bg-white p-5 rounded-2xl shadow hover:shadow-xl text-center"
              >
              <img
                src={s.img}
                alt={s.name}
                className="mx-auto w-28 h-28 rounded-full object-cover"
                loading="lazy"
              />
              <h3 className="mt-4 font-semibold">{s.name}</h3>
              <p className="text-green-700 text-sm">{s.role}</p>
            </motion.div>
            ))}
          </div>
        </motion.section>

       {/* COURSE STRUCTURE */}
        <motion.section
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          className="bg-white rounded-2xl shadow-lg p-8"
        >
          <h2 className="text-2xl font-bold flex items-center gap-3">
            <GraduationCap /> Course Structure (HND)
          </h2>

          <Divider />

          <div className="grid md:grid-cols-2 gap-6">
            {/* Year 1 */}
            <div className="bg-gray-50 p-4 rounded hover:shadow-md transition">
              <h3 className="font-semibold mb-2">Year 1</h3>
              <ol className="list-decimal list-inside space-y-1">
                {year1.map((c, i) => (
                  <li key={i}>{c}</li>
                ))}
              </ol>
            </div>

            {/* Year 2 */}
            <div className="bg-gray-50 p-4 rounded hover:shadow-md transition">
              <h3 className="font-semibold mb-2">Year 2</h3>
              <ol className="list-decimal list-inside space-y-1">
                {year2.map((c, i) => (
                  <li key={i}>{c}</li>
                ))}
              </ol>
            </div>
          </div>
        </motion.section>

        {/* Brochure Slider + Download */}
          <motion.section
            id="brochure"
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="bg-blue-900 text-white rounded-2xl shadow-lg p-6"
          >
            <div className="flex items-center justify-between flex-wrap gap-4">
              <h2 className="text-2xl font-bold flex items-center gap-3">
                <File className="text-white" /> Admission Brochure
              </h2>

              <a
                href="/brochure.pdf"
                download
                className="inline-flex items-center gap-2 bg-white text-blue-900 px-4 py-2 rounded-md hover:bg-gray-100 transition"
              >
                <Download /> Download PDF
              </a>
            </div>

            <Divider />

            {/* Horizontal slider with snap */}
            <div className="mt-4 overflow-x-auto no-scrollbar py-4">
              <div className="flex gap-4 w-max snap-x snap-mandatory">
                {brochureSlides.map((s, i) => (
                  <motion.div
                    key={i}
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.97 }}
                    className="snap-center min-w-[300px] md:min-w-[360px] bg-white/10 rounded-lg p-4 hover:bg-white/15 transition"
                  >
                    <img
                      src={s.img}
                      alt={s.title}
                      className="w-full h-40 object-cover rounded-md shadow-sm"
                      loading="lazy"
                    />
                    <h4 className="mt-3 font-semibold">{s.title}</h4>
                    <p className="text-sm mt-1 text-gray-100">
                      Short highlight about this brochure page to entice prospective students.
                    </p>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.section>


        {/* GALLERY */}
          <motion.section
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            className="bg-white rounded-2xl shadow-lg p-8"
          >
            <h2 className="text-2xl font-bold flex items-center gap-3">
              <ImageIcon /> Department Gallery
            </h2>

            <Divider />

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {galleryImages.map((item, i) => (
                <motion.div
                  key={i}
                  onClick={() => setActiveIndex(i)}
                  className="relative overflow-hidden rounded-lg shadow cursor-pointer group"
                  whileHover={{ scale: 1.04 }}
                  whileTap={{ scale: 0.95 }}
                  transition={{ type: "spring", stiffness: 200, damping: 14 }}
                >
                  {/* Blur placeholder */}
                  <img
                    src={item.img}
                    alt="gallery"
                    loading="lazy"
                    className="w-full h-40 object-cover blur-sm scale-110 absolute inset-0"
                  />

                  {/* Actual image */}
                  <img
                    src={item.img}
                    alt="gallery"
                    loading="lazy"
                    className="w-full h-40 object-cover rounded-lg relative transition-opacity duration-700 group-hover:scale-105"
                    onLoad={(e) => {
                      e.currentTarget.previousSibling.classList.add("opacity-0");
                    }}
                  />
                </motion.div>
              ))}
            </div>
          </motion.section>

        {/* Contact */}
                <motion.section id="contact" variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="bg-white rounded-2xl shadow-lg p-8">
                  <h2 className="text-2xl font-bold flex items-center gap-3"><MapPin className="text-blue-700" /> Contact the Department</h2>
        
                  <Divider />
        
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <p className="text-gray-700"><strong>Address:</strong> Federal Polytechnic, Bida, Niger State, Nigeria</p>
                      <p className="text-gray-700 flex items-center gap-2 mt-2"><Mail className="text-green-700" /> renewableenergy@fedpolybida.edu.ng</p>
                      <p className="text-gray-700 flex items-center gap-2 mt-2"><Phone className="text-blue-700" /> +234 703 614 5748</p>
                    </div>
        
                    <div>
                      <iframe title="location" src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3931.203503579147!2d6.0079231!3d9.0391016!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x104966b7f2e9b195%3A0x18c8eec7a2fae37c!2sFederal+Polytechnic+Bida%2C+Doko+Road%2C+Bida%2C+Niger+State!5e0!3m2!1sen!2sus!4v1694784000000!5m2!1sen!2sus" width="100%" height="220" className="rounded-md border-0 shadow-sm"></iframe>
                    </div>
                  </div>
                </motion.section>

                {/* LIGHTBOX MODAL */}
{activeIndex !== null && (
  <motion.div
    className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center px-4"
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    onClick={() => setActiveIndex(null)}
  >
    {/* PREVIOUS */}
    <button
      onClick={(e) => {
        e.stopPropagation();
        prevImage();
      }}
      className="absolute left-4 md:left-10 text-white text-4xl font-bold opacity-80 hover:opacity-100 transition"
    >
      ‹
    </button>

    {/* IMAGE */}
    <motion.img
      key={activeIndex}
      src={galleryImages[activeIndex].img}
      alt="Full view"
      className="max-w-full max-h-[90vh] rounded-xl shadow-2xl"
      initial={{ scale: 0.9, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ duration: 0.3, ease: "easeOut" }}
      drag="x"
      dragConstraints={{ left: 0, right: 0 }}
      dragElastic={0.15}
      onDragEnd={(e, info) => {
        if (info.offset.x < -100) nextImage();
        if (info.offset.x > 100) prevImage();
      }}
      onClick={(e) => e.stopPropagation()}
    />

    {/* NEXT */}
    <button
      onClick={(e) => {
        e.stopPropagation();
        nextImage();
      }}
      className="absolute right-4 md:right-10 text-white text-4xl font-bold opacity-80 hover:opacity-100 transition"
    >
      ›
    </button>
  </motion.div>
)}

      </main>
    </div>
  );
};

export default RenewableEnergyEngineering;
